import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  ClickAwayListener,
  Popover,
  Typography,
  TextField,
  InputAdornment,
  SxProps,
  Theme,
  Tooltip
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import CircularProgress from '@mui/material/CircularProgress';

export interface DropdownPopoverProps<T = any> {
  /**
   * Currently selected value
   */
  selectedValue: T;
  /**
   * Array of options to display in the dropdown
   */
  options: T[];
  /**
   * Callback function when an option is selected
   */
  onSelect: (value: T) => void;
  /**
   * Whether the options array contains objects
   */
  isArrayOfObject?: boolean;
  /**
   * Key to use for displaying label when options are objects
   */
  labelKey?: string;
  /**
   * Key to use for identifying items when options are objects
   */
  idKey?: string;
  /**
   * Whether to wrap labels in double quotes
   */
  isDoubleQuotes?: boolean;
  /**
   * Icon to display before selected item (string path or ReactNode)
   */
  startIcon?: React.ReactNode | string;
  /**
   * Custom styles for different parts of the component (optional - will use defaults if not provided)
   */
  styles?: {
    filterBoxSx?: SxProps<Theme>;
    selectedFilterSx?: SxProps<Theme>;
    optionBox?: SxProps<Theme>;
    selectedOptionText?: SxProps<Theme>;
    nonSelectedOptionText?: SxProps<Theme>;
  };
  /**
   * Placeholder text when no value is selected
   */
  placeholder?: string;
  /**
   * Whether the dropdown is disabled
   */
  disabled?: boolean;
  /**
   * Label text to display above the dropdown
   */
  label?: string;
  /**
   * Custom sx styles for the label
   */
  labelSx?: SxProps<Theme>;
  /**
   * Whether the field is required (shows asterisk)
   */
  required?: boolean;
  /**
   * Enable search/filter functionality (default: true if options.length > 5)
   */
  enableSearch?: boolean;
  /**
   * Placeholder text for the search input
   */
  searchPlaceholder?: string;
  /**
   * Custom footer content to render at the bottom of the popover
   */
  footer?: React.ReactNode;
  /**
   * Custom render function for each option (overrides default rendering)
   */
  renderOption?: (option: T, isSelected: boolean) => React.ReactNode;
  /**
   * Custom function to determine if an individual option is disabled
   */
  isOptionDisabled?: (option: T) => boolean;
  /**
   * Optional tooltip message to display when an option is disabled
   */
  getOptionDisabledTooltip?: (option: T) => string;
  /**
   * Custom function to extract searchable text from option (useful when options are React nodes)
   */
  getSearchText?: (option: T) => string;
  /**
   * Whether the dropdown is in a loading state
   */
  loading?: boolean;
}

// Default label styles
const defaultLabelSx: SxProps<Theme> = {
  fontFamily: 'Inter',
  fontWeight: 500,
  fontSize: { xs: '14px', sm: '14px' },
  lineHeight: { xs: '20px', sm: '20px' },
  color: 'var(--color-text-secondary)',
  marginBottom: '6px',
  display: 'block'
};

// Default styles based on common usage patterns
const defaultStyles = {
  filterBoxSx: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    backgroundColor: 'white',
    minWidth: '170px',
    minHeight: '44px',
    borderRadius: '6px',
    padding: '8px 16px 8px 16px',
    border: '1px solid var(--color-border-muted)',
    gap: '16px'
  },
  selectedFilterSx: {
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: 'var(--font-size-14)',
    lineHeight: 'var(--line-height-20)',
    color: 'var(--color-primary)'
  },
  optionBox: {
    px: 2,
    py: 1,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'var(--color-bg-grey)'
    }
  },
  selectedOptionText: {
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: 'var(--font-size-14)',
    lineHeight: 'var(--line-height-20)',
    color: 'var(--color-primary)'
  },
  nonSelectedOptionText: {
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: 'var(--font-size-14)',
    lineHeight: 'var(--line-height-20)',
    color: 'var(--color-text-dark2)'
  }
};

/**
 * Reusable DropdownPopover component with support for primitive values and objects
 * Similar to FilterDropDownPopover but located in the UI components folder
 */
const DropdownPopover = <T,>({
  selectedValue,
  options,
  onSelect,
  isArrayOfObject = false,
  labelKey = '',
  idKey = '',
  isDoubleQuotes = false,
  startIcon,
  styles,
  placeholder = '',
  disabled = false,
  label,
  labelSx,
  required = false,
  enableSearch,
  searchPlaceholder = 'Search...',
  footer,
  renderOption,
  isOptionDisabled,
  getOptionDisabledTooltip,
  getSearchText,
  loading = false
}: DropdownPopoverProps<T>) => {
  const ENABLE_SEARCH_THRESHOLD = 10;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const isOpen = Boolean(anchorEl);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Auto-enable search if options.length > 5, unless explicitly set
  const shouldShowSearch =
    enableSearch !== undefined
      ? enableSearch
      : options.length > ENABLE_SEARCH_THRESHOLD;

  // Reset search and highlight when popover closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  // Auto-focus search input when popover opens
  useEffect(() => {
    if (isOpen && shouldShowSearch && searchInputRef.current) {
      // Small delay to ensure the popover is fully rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, shouldShowSearch]);

  // Merge default styles with provided styles
  const mergedStyles = {
    filterBoxSx: { ...defaultStyles.filterBoxSx, ...styles?.filterBoxSx },
    selectedFilterSx: {
      ...defaultStyles.selectedFilterSx,
      ...styles?.selectedFilterSx
    },
    optionBox: { ...defaultStyles.optionBox, ...styles?.optionBox },
    selectedOptionText: {
      ...defaultStyles.selectedOptionText,
      ...styles?.selectedOptionText
    },
    nonSelectedOptionText: {
      ...defaultStyles.nonSelectedOptionText,
      ...styles?.nonSelectedOptionText
    }
  };

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled || loading) return;
    setAnchorEl(isOpen ? null : event.currentTarget);
  };

  const handleSelect = (value: T) => {
    if (isOptionDisabled && isOptionDisabled(value)) {
      return;
    }
    try {
      onSelect(value);
      setAnchorEl(null);
    } catch (error) {
      // Silently handle errors
    }
  };

  const getLabel = (item: T): string => {
    try {
      if (
        isArrayOfObject &&
        typeof item === 'object' &&
        item !== null &&
        labelKey
      ) {
        const val = (item as Record<string, any>)[labelKey];
        // Explicitly check if 'val' is a string
        return typeof val === 'string' ? val : '';
      }
      // For the fallback, only return 'item' if it's already a string
      return typeof item === 'string' ? item : '';
    } catch (error) {
      return '';
    }
  };

  const selectedLabel = getLabel(selectedValue);

  // Helper to check if an item is a React node
  const isReactElement = (item: T): boolean =>
    React.isValidElement(item as unknown as React.ReactElement);

  // Render option content (handles React nodes, strings, and custom render)
  const renderOptionContent = (
    option: T,
    isSelected: boolean
  ): React.ReactNode => {
    if (renderOption) {
      return renderOption(option, isSelected);
    }

    if (isReactElement(option)) {
      return <>{(option as unknown) as React.ReactNode}</>;
    }

    const optionLabel = getLabel(option);
    return (
      <Typography
        sx={
          isSelected
            ? mergedStyles.selectedOptionText
            : mergedStyles.nonSelectedOptionText
        }
      >
        {isDoubleQuotes && optionLabel
          ? `"${optionLabel}"`
          : optionLabel || placeholder}
      </Typography>
    );
  };

  // Render selected value in trigger (handles React nodes)
  const renderSelectedValue = (): React.ReactNode => {
    if (isReactElement(selectedValue)) {
      return (
        <Box sx={{ flex: 1 }}>
          {(selectedValue as unknown) as React.ReactNode}
        </Box>
      );
    }

    return (
      <Typography sx={{ flex: 1, ...mergedStyles.selectedFilterSx }}>
        {isDoubleQuotes && selectedLabel
          ? `"${selectedLabel}"`
          : selectedLabel || placeholder}
      </Typography>
    );
  };

  // Render startIcon (string path or ReactNode)
  const renderStartIcon = (): React.ReactNode | null => {
    if (!startIcon) return null;

    if (typeof startIcon === 'string') {
      return (
        <Box
          component="img"
          src={startIcon}
          alt=""
          sx={{
            width: 20,
            height: 20,
            objectFit: 'contain',
            flexShrink: 0
          }}
        />
      );
    }

    return <>{startIcon}</>;
  };

  // Helper to get the unique ID
  const getItemId = (item: T): any => {
    try {
      if (typeof item === 'object' && item !== null && idKey) {
        return (item as Record<string, any>)[idKey];
      }
      return item; // Fallback for primitive values
    } catch (error) {
      return item;
    }
  };

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!shouldShowSearch || !searchTerm.trim()) {
      return options;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    return options.filter((option) => {
      // Use custom search text function if provided
      if (getSearchText) {
        try {
          const searchText = getSearchText(option);
          return searchText.toLowerCase().includes(searchLower);
        } catch (error) {
          return false;
        }
      }

      // For React nodes without getSearchText, skip filtering (show all)
      if (isReactElement(option)) {
        return true;
      }

      // Default behavior for strings/objects
      let optionLabel = '';
      try {
        if (
          isArrayOfObject &&
          typeof option === 'object' &&
          option !== null &&
          labelKey
        ) {
          const val = (option as Record<string, any>)[labelKey];
          optionLabel = typeof val === 'string' ? val : '';
        } else {
          optionLabel = typeof option === 'string' ? option : '';
        }
      } catch (error) {
        optionLabel = '';
      }
      return optionLabel.toLowerCase().includes(searchLower);
    });
  }, [
    options,
    searchTerm,
    shouldShowSearch,
    isArrayOfObject,
    labelKey,
    getSearchText
  ]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [highlightedIndex]);

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || filteredOptions.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < filteredOptions.length
        ) {
          handleSelect(filteredOptions[highlightedIndex]);
        } else if (filteredOptions.length > 0) {
          // If no option is highlighted, select the first one
          handleSelect(filteredOptions[0]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        setAnchorEl(null);
        break;
    }
  };

  // Merge label styles
  const mergedLabelSx = labelSx
    ? ([defaultLabelSx, labelSx] as SxProps<Theme>)
    : defaultLabelSx;

  return (
    <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
      <Box sx={{ width: '100%' }}>
        {/* Label */}
        {label && (
          <Typography sx={mergedLabelSx} component="label">
            {label}
            {required && (
              <Box
                component="span"
                sx={{ color: 'var(--color-text-secondary)', marginLeft: '4px' }}
              >
                *
              </Box>
            )}
          </Typography>
        )}
        {/* Trigger */}
        <Box
          ref={triggerRef}
          onClick={handleToggle}
          sx={{
            ...mergedStyles.filterBoxSx,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: disabled || loading ? 'not-allowed' : 'pointer',
            opacity: disabled || loading ? 0.6 : 1,
            pointerEvents: disabled || loading ? 'none' : 'auto'
          }}
        >
          {startIcon && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {renderStartIcon()}
            </Box>
          )}
          {renderSelectedValue()}
          {loading ? (
            <CircularProgress size={16} sx={{ color: 'var(--color-primary)' }} />
          ) : isOpen ? (
            <ExpandLessIcon fontSize="small" sx={{ color: 'var(--color-primary)' }} />
          ) : (
            <ExpandMoreIcon fontSize="small" sx={{ color: 'var(--color-primary)' }} />
          )}
        </Box>

        {/* Popover */}
        <Popover
          open={isOpen}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: triggerRef.current?.clientWidth || 240,
              borderRadius: '6px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              py: '4px'
            }
          }}
          disableScrollLock
        >
          <Box>
            {/* Search Input */}
            {shouldShowSearch && (
              <Box sx={{ px: 2, py: 1, borderBottom: '1px solid var(--color-border-grey)' }}>
                <TextField
                  inputRef={searchInputRef}
                  fullWidth
                  size="small"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={handleKeyDown}
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{ fontSize: 'var(--font-size-18)', color: 'var(--color-text-placeholder)' }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchTerm('');
                          }}
                          sx={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            color: 'var(--color-text-placeholder)',
                            '&:hover': { color: 'var(--color-primary)' }
                          }}
                        >
                          <ClearIcon sx={{ fontSize: 'var(--font-size-18)' }} />
                        </Box>
                      </InputAdornment>
                    ) : null
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: 'Inter',
                      fontSize: 'var(--font-size-14)',
                      '& fieldset': {
                        borderColor: 'var(--color-border-grey)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'var(--color-border-muted)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'var(--color-primary)',
                        borderWidth: '1px'
                      }
                    },
                    '& .MuiInputBase-input': {
                      py: '8px',
                      color: 'var(--color-text-input)'
                    }
                  }}
                />
              </Box>
            )}
            {/* Options List */}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                // Efficient 'isSelected' check
                const isSelected =
                  getItemId(option) === getItemId(selectedValue);
                const isHighlighted = highlightedIndex === index;
                const isOptDisabled = Boolean(isOptionDisabled && isOptionDisabled(option));
                const disabledTooltip = isOptDisabled && getOptionDisabledTooltip ? getOptionDisabledTooltip(option) : '';

                const optionElement = (
                  <Box
                    key={index}
                    ref={(el: HTMLDivElement | null) => {
                      optionRefs.current[index] = el;
                    }}
                    onClick={() => !isOptDisabled && handleSelect(option)}
                    onMouseEnter={() => !isOptDisabled && setHighlightedIndex(index)}
                    sx={{
                      ...mergedStyles.optionBox,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: isOptDisabled ? 'not-allowed' : 'pointer',
                      opacity: isOptDisabled ? 0.45 : 1,
                      pointerEvents: isOptDisabled ? 'auto' : 'auto',
                      ...(!isOptDisabled && isHighlighted && {
                        backgroundColor: 'var(--color-bg-grey)'
                      })
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      {renderOptionContent(option, isSelected)}
                    </Box>
                    {isSelected && (
                      <CheckIcon fontSize="small" color="primary" />
                    )}
                  </Box>
                );

                if (disabledTooltip) {
                  return (
                    <Tooltip key={index} title={disabledTooltip} placement="right" arrow>
                      <div>{optionElement}</div>
                    </Tooltip>
                  );
                }

                return optionElement;
              })
            ) : (
              <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontSize: 'var(--font-size-14)',
                    color: 'var(--color-text-placeholder)'
                  }}
                >
                  No results found
                </Typography>
              </Box>
            )}
            {/* Footer */}
            {footer && (
              <Box
                sx={{
                  borderTop: '1px solid var(--color-border-grey)',
                  px: 2,
                  py: 1
                }}
              >
                {footer}
              </Box>
            )}
          </Box>
        </Popover>
      </Box>
    </ClickAwayListener>
  );
};

export default DropdownPopover;
